/**
 * demoScript — the choreography behind the two narration features:
 *
 *  1. "Simulează o comandă" — a one-click, self-contained cinematic that drives
 *     a single order through the WHOLE Exemplu SRL flow (mesaj → comandă →
 *     confirmare → pregătire → dispecerizare → livrare → factură), writing to
 *     Dexie and letting the real screens react via `useLiveQuery`, narrated in
 *     plain Romanian at every beat.
 *
 *  2. "Tur ghidat" — a rich, screen-by-screen tour (`RICH_TOUR`).
 *
 * This module owns everything that is testable and framework-agnostic: the
 * narration copy, the pausable/skippable timing controller, and the Dexie
 * mutations. The React glue (overlay + navigation) lives in DemoHost.tsx.
 *
 * Robust & repeatable: the cinematic runs on its OWN synthetic message/order
 * (fixed ids `msg-demo` / `cmd-demo`) that it cleans up and recreates on every
 * run — so it never depends on pristine seed state and always plays through,
 * even on the tenth run.
 */

import type { Oras, RouteStop } from '@/components/RouteMap'
import type { TourStep } from '@/context'
import { type Comanda, type ComandaLinie, type Mesaj, db } from '@/db/dexie'
import {
  addActivitate,
  advanceComanda,
  genereazaFactura,
  getClient,
  getFacturaByComanda,
  getProduse,
  setSoferMasina,
  totalsFromLinii,
} from '@/db/queries'
import { azi, isoZi } from '@/lib/format'
import { parseOrder } from '@/lib/parseOrder'

// ---------------------------------------------------------------------------
//  Constants — the synthetic demo subjects
// ---------------------------------------------------------------------------
export const DEMO_MSG_ID = 'msg-demo'
export const DEMO_CMD_ID = 'cmd-demo'
/** star client — Măcelăria Crișul (recurrent, has a preț special on P01) */
export const DEMO_CLIENT_ID = 'c1'
/** an available driver + refrigerated van, chosen so dispatch never fails */
export const DEMO_SOFER_ID = 's2'
export const DEMO_MASINA_ID = 'm1'

/** the exact WhatsApp text the demo runs on (matches the seed star message) */
export const STAR_TEXT =
  'Bună ziua, pt mâine: 20kg piept dezosat, 15kg pulpe superioare, 10kg aripioare, 5 tăvi ficăței. Mulțumesc!'

const nowIso = () => new Date().toISOString()

// ---------------------------------------------------------------------------
//  Narration — plain Romanian, each beat says what happened & why it beats
//  a WhatsApp thread plus a paper notebook.
// ---------------------------------------------------------------------------
export const CINEMATIC_CHAPTERS = [
  'Mesaj',
  'Transformare',
  'Flux',
  'Dispecerizare',
  'Livrare',
  'Facturare',
  'Gata',
] as const

export const NARRATION = {
  intro: {
    title: 'De la mesaj la livrare',
    text: 'Urmărește o singură comandă cum trece prin tot fluxul Exemplu SRL — de la un mesaj, până la factură.',
  },
  mesaj: {
    title: 'Un mesaj pe WhatsApp',
    text: 'Clientul scrie ca de obicei: text liber, în grabă, cu prescurtări. Așa vin comenzile, zi de zi.',
  },
  transform: {
    title: 'Devine comandă, pe loc',
    text: 'Fără caiet și fără greșeli: clientul e recunoscut, produsele și cantitățile sunt citite automat, iar prețul special e aplicat.',
  },
  fluxNou: {
    title: 'Comanda intră în flux',
    text: 'Apare pe tablou ca „Nou". Nimic nu se pierde printre conversații — totul e într-un singur loc.',
  },
  confirmat: {
    title: 'Comandă confirmată',
    text: 'Trece în „Confirmat" — clientul știe pe loc că a fost preluată, fără zeci de mesaje înainte și înapoi.',
  },
  pregatire: {
    title: 'Se pregătește în depozit',
    text: 'Intră „În pregătire": cântărire și ambalare, cu totul urmărit pe tablou.',
  },
  dispecerizare: {
    title: 'Șofer și mașină, alocate',
    text: 'Un șofer liber și o mașină frigorifică sunt alocate, cu pornire din depozitul din Oradea.',
  },
  pedrum: {
    title: 'Pleacă pe traseu',
    text: 'Urcă în „Pe drum" — lanț de frig neîntrerupt, din depozit până la client.',
  },
  livrare: {
    title: 'Livrare urmărită în timp real',
    text: 'Camionul livrează opririle pe rând, pe harta Bihorului. Fiecare oprire e bifată automat.',
  },
  livrat: {
    title: 'Comandă livrată',
    text: 'Toate opririle au fost livrate — comanda e marcată „Livrat", fără niciun telefon de verificare.',
  },
  factura: {
    title: 'Factura pleacă la ANAF',
    text: 'Se emite automat, cu TVA 9%, și e trimisă prin e-Factura. Pentru carne, codul UIT de e-Transport e generat din start.',
  },
  outro: {
    title: 'Totul într-un singur loc',
    text: 'Un mesaj, o comandă, o livrare, o factură. Așa lucrează Exemplu SRL — de la mesaj la livrare.',
  },
} as const

// ---------------------------------------------------------------------------
//  Guided tour — one rich step per screen, plus a closing recap.
// ---------------------------------------------------------------------------
export const RICH_TOUR: TourStep[] = [
  {
    tab: 'panou',
    titlu: 'Panoul de control',
    text: 'Toată ziua dintr-o privire: comenzi, kilograme livrate, încasări și mașini pe traseu. În loc de zeci de conversații și un caiet, ai un singur tablou.',
  },
  {
    tab: 'mesaje',
    anchor: 'btn-transforma',
    titlu: 'De la mesaj la comandă',
    text: 'Mesajele de pe WhatsApp ajung aici. Cu un clic devin comenzi prețuite — produsele și cantitățile sunt recunoscute automat, fără să rescrii nimic de mână.',
  },
  {
    tab: 'comenzi',
    anchor: 'comenzi-board',
    titlu: 'Fluxul comenzilor',
    text: 'Fiecare comandă trece prin pași clari: Nou, Confirmat, În pregătire, Pe drum, Livrat, Facturat. Vezi oricând unde e fiecare comandă.',
  },
  {
    tab: 'clienti',
    titlu: 'Clienți și istoricul lor',
    text: 'Toți clienții la un loc, cu comanda uzuală, prețul special și soldul restant. Nu mai ții minte pe cine cât costă — știe aplicația.',
  },
  {
    tab: 'catalog',
    titlu: 'Catalog și prețuri',
    text: 'Produsele, prețul pe kilogram și stocul. Schimbi un preț o singură dată, iar comenzile noi îl folosesc automat. Stocul scăzut e semnalat.',
  },
  {
    tab: 'flota',
    titlu: 'Flotă și trasee',
    text: 'Mașini, șoferi și trasee pe harta Bihorului. Aloci un șofer și o mașină frigorifică, iar livrările se urmăresc oprire cu oprire.',
  },
  {
    tab: 'facturare',
    titlu: 'Facturare și e-Factura',
    text: 'Din livrare iese factura, cu TVA 9% și trimitere automată la ANAF prin e-Factura. Pentru carne, codul UIT de e-Transport e generat din start.',
  },
  {
    tab: 'rapoarte',
    titlu: 'Rapoarte',
    text: 'Vânzări pe produs, pe client și pe zi. Vezi ce se vinde și cine cumpără, fără să aduni nimic manual din caiet.',
  },
  {
    tab: 'panou',
    titlu: 'Gata de probă',
    text: 'Ăsta e tot fluxul, într-un singur loc. Apasă „Simulează o comandă" ca să vezi o comandă cum trece de la mesaj până la livrare și factură.',
  },
]

// ---------------------------------------------------------------------------
//  Cinematic stage descriptor (rendered by the overlay)
// ---------------------------------------------------------------------------
export type CinematicStage =
  | { kind: 'intro' }
  | { kind: 'phone'; typing: boolean }
  | { kind: 'transform'; linii: ComandaLinie[] }
  | { kind: 'route'; stops: RouteStop[] }
  | { kind: 'invoice'; facturaId: string }
  | { kind: 'outro' }

// ---------------------------------------------------------------------------
//  Timing controller — pausable / skippable / cancelable delays.
//
//  `sleep(ms)` freezes while paused, resolves instantly on skip, and returns
//  promptly on cancel. `until(pred)` polls with the same guarantees (used to
//  wait for the truck to finish its route). One controller per cinematic run.
// ---------------------------------------------------------------------------
export class CinematicController {
  cancelled = false
  paused = false
  private skipRequested = false
  private resumers: Array<() => void> = []
  private tickResolve: (() => void) | null = null
  private tickTimer: ReturnType<typeof setTimeout> | null = null
  /** notified whenever pause state flips (the overlay mirrors it + the truck) */
  onPaused: ((paused: boolean) => void) | null = null

  pause(): void {
    if (this.cancelled || this.paused) return
    this.paused = true
    this.onPaused?.(true)
  }

  resume(): void {
    if (this.cancelled || !this.paused) return
    this.paused = false
    this.onPaused?.(false)
    this.releasePause()
  }

  togglePause(): void {
    if (this.paused) this.resume()
    else this.pause()
  }

  skip(): void {
    this.skipRequested = true
    this.wake()
    this.releasePause()
  }

  cancel(): void {
    this.cancelled = true
    this.wake()
    this.releasePause()
  }

  private wake(): void {
    if (this.tickTimer) {
      clearTimeout(this.tickTimer)
      this.tickTimer = null
    }
    const r = this.tickResolve
    this.tickResolve = null
    r?.()
  }

  private releasePause(): void {
    const rs = this.resumers
    this.resumers = []
    for (const r of rs) r()
  }

  private waitWhilePaused(): Promise<void> {
    if (this.cancelled || !this.paused) return Promise.resolve()
    return new Promise((resolve) => this.resumers.push(resolve))
  }

  private tick(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.tickResolve = resolve
      this.tickTimer = setTimeout(() => {
        this.tickTimer = null
        this.tickResolve = null
        resolve()
      }, ms)
    })
  }

  /** Interruptible delay. Resolves early on skip/cancel; freezes while paused. */
  async sleep(ms: number): Promise<void> {
    this.skipRequested = false
    const TICK = 90
    let elapsed = 0
    while (elapsed < ms) {
      if (this.cancelled || this.skipRequested) break
      await this.waitWhilePaused()
      if (this.cancelled || this.skipRequested) break
      const step = Math.min(TICK, ms - elapsed)
      await this.tick(step)
      elapsed += step
    }
    this.skipRequested = false
  }

  /** Poll until `pred()` is true (or a safety timeout). Same interrupt rules. */
  async until(pred: () => boolean, timeoutMs = 30_000): Promise<void> {
    this.skipRequested = false
    const start = Date.now()
    while (!pred() && Date.now() - start < timeoutMs) {
      if (this.cancelled || this.skipRequested) break
      await this.waitWhilePaused()
      if (this.cancelled || this.skipRequested) break
      await this.tick(120)
    }
    this.skipRequested = false
  }
}

// ---------------------------------------------------------------------------
//  Dexie mutations — the actual "the app runs itself" writes.
// ---------------------------------------------------------------------------

/** Remove any leftover demo message/order/invoice so each run starts clean. */
export async function cleanupDemoArtifacts(): Promise<void> {
  const factura = await getFacturaByComanda(DEMO_CMD_ID)
  await db.transaction('rw', db.mesaje, db.comenzi, db.facturi, async () => {
    if (factura) await db.facturi.delete(factura.id)
    await db.comenzi.delete(DEMO_CMD_ID)
    await db.mesaje.delete(DEMO_MSG_ID)
  })
}

/** Drop the fresh incoming WhatsApp message into the inbox (status "nou"). */
export async function seedDemoMessage(): Promise<void> {
  const mesaj: Mesaj = {
    id: DEMO_MSG_ID,
    clientId: DEMO_CLIENT_ID,
    telefon: '0721 445 190',
    numeAfisat: 'Măcelăria Crișul',
    text: STAR_TEXT,
    ora: nowIso(),
    status: 'nou',
  }
  await db.mesaje.put(mesaj)
}

/** Parse the star text into priced lines (client c1's preț special applied). */
export async function buildDemoLinii(): Promise<ComandaLinie[]> {
  const [client, produse] = await Promise.all([getClient(DEMO_CLIENT_ID), getProduse()])
  const { linii } = parseOrder(STAR_TEXT, produse, { pretSpecial: client?.pretSpecial })
  const mapped: ComandaLinie[] = linii.map((l) => ({
    produsId: l.produsId,
    cod: l.cod,
    nume: l.nume,
    cantitateKg: l.cantitateKg,
    pretKg: l.pretKg,
    um: l.um,
  }))
  if (mapped.length > 0) return mapped

  // extremely defensive fallback — the client's usual order
  const byId = new Map(produse.map((p) => [p.id, p]))
  return (client?.comandaUzuala ?? []).flatMap((u) => {
    const p = byId.get(u.produsId)
    if (!p) return []
    return [
      {
        produsId: p.id,
        cod: p.cod,
        nume: p.nume,
        cantitateKg: u.cantitateKg,
        pretKg: client?.pretSpecial?.[p.id] ?? p.pretKg,
        um: p.um,
      },
    ]
  })
}

/** A plausible e-Transport UIT code (meat delivery). */
function demoUIT(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  const rand = String(Math.floor(Math.random() * 900) + 100)
  return `2RO${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}00${rand}`
}

/** Create the demo order as "Nou" (delivered today, meat → carries a UIT). */
export async function createDemoOrder(linii: ComandaLinie[]): Promise<void> {
  const client = await getClient(DEMO_CLIENT_ID)
  const iso = nowIso()
  const comanda: Comanda = {
    id: DEMO_CMD_ID,
    clientId: DEMO_CLIENT_ID,
    dataComanda: iso,
    dataLivrare: isoZi(azi()),
    status: 'Nou',
    linii,
    ...totalsFromLinii(linii),
    adresaLivrare: client?.adresa ?? '',
    mesajOriginal: STAR_TEXT,
    codUIT: demoUIT(),
    timeline: [{ status: 'Nou', ora: iso }],
  }
  await db.comenzi.put(comanda)
  await addActivitate({
    tip: 'comanda',
    icon: 'MessageSquarePlus',
    text: `Comandă nouă din mesaj — ${client?.nume ?? 'Măcelăria Crișul'}`,
  })
}

/** Link the message to the new order and mark it "transformat". */
export async function markDemoMessageTransformed(): Promise<void> {
  await db.mesaje.update(DEMO_MSG_ID, { status: 'transformat', comandaId: DEMO_CMD_ID })
}

/** Advance the demo order one funnel step (with all the usual side-effects). */
export async function advanceDemoOrder(): Promise<void> {
  await advanceComanda(DEMO_CMD_ID)
}

/** Assign the available driver + refrigerated van to the demo order. */
export async function dispatchDemoOrder(): Promise<void> {
  await setSoferMasina(DEMO_CMD_ID, DEMO_SOFER_ID, DEMO_MASINA_ID)
}

/** Generate the invoice (TVA 9% + e-Factura + advances to Facturat). */
export async function invoiceDemoOrder(): Promise<string | undefined> {
  return genereazaFactura(DEMO_CMD_ID)
}

/** A short, always-good-looking Bihor route that ends at the star client. */
export function demoRouteStops(): RouteStop[] {
  const stops: { id: string; eticheta: string; oras: Oras }[] = [
    { id: 'demo-stop-marghita', eticheta: 'Măcelăria Tradiția', oras: 'marghita' },
    { id: 'demo-stop-beius', eticheta: 'Alimentara Central', oras: 'beius' },
    { id: DEMO_CMD_ID, eticheta: 'Măcelăria Crișul', oras: 'oradea' },
  ]
  return stops
}
